import * as React from "react";
import { cn } from "./utils";

interface LoaderProps extends React.ComponentProps<"div"> {
  size?: "sm" | "md" | "lg";
  variant?: "default" | "primary" | "subtle";
}

const sizeMap = {
  sm: 16,
  md: 32,
  lg: 48,
};

const borderSizeMap = {
  sm: 2,
  md: 3,
  lg: 4,
};

const colorMap = {
  primary: {
    border: "#dbeafe",
    borderTop: "#17265a",
  },
  default: {
    border: "#e5e7eb",
    borderTop: "#17265a",
  },
  subtle: {
    border: "#f3f4f6",
    borderTop: "#17265a",
  },
};

export function Loader({
  className,
  size = "md",
  variant = "primary",
  style,
  ...props
}: LoaderProps) {
  const sizePx = sizeMap[size];
  const borderPx = borderSizeMap[size];
  const colors = colorMap[variant];

  const loaderStyle: React.CSSProperties = {
    width: `${sizePx}px`,
    height: `${sizePx}px`,
    border: `${borderPx}px solid ${colors.border}`,
    borderTop: `${borderPx}px solid ${colors.borderTop}`,
    borderRadius: "50%",
    animation: "loader-spin 0.8s linear infinite",
    display: "inline-block",
    ...style,
  };

  return (
    <div
      className={cn(className)}
      style={loaderStyle}
      role="status"
      aria-label="Loading"
      {...props}
    >
      <span className="sr-only">Loading...</span>
    </div>
  );
}

interface LoaderOverlayProps extends React.ComponentProps<"div"> {
  size?: "sm" | "md" | "lg";
  variant?: "default" | "primary" | "subtle";
  text?: string;
}

export function LoaderOverlay({
  className,
  size = "md",
  variant = "primary",
  text,
  ...props
}: LoaderOverlayProps) {
  return (
    <div
      className={cn(
        "fixed inset-0 z-50 flex items-center justify-center bg-white/90 backdrop-blur-[2px]",
        className
      )}
      {...props}
    >
      <div className="flex flex-col items-center gap-4">
        <Loader size={size} variant={variant} />
        {text && (
          <p className="text-sm font-medium text-gray-700 tracking-wide">
            {text}
          </p>
        )}
      </div>
    </div>
  );
}

interface LoaderInlineProps extends React.ComponentProps<"div"> {
  size?: "sm" | "md" | "lg";
  variant?: "default" | "primary" | "subtle";
  text?: string;
}

export function LoaderInline({
  className,
  size = "md",
  variant = "primary",
  text,
  ...props
}: LoaderInlineProps) {
  return (
    <div className={cn("flex items-center gap-3", className)} {...props}>
      <Loader size={size} variant={variant} />
      {text && (
        <span className="text-sm font-medium text-gray-700">{text}</span>
      )}
    </div>
  );
}
